import type { Messages } from "@lingui/core"
import { i18n } from "@lingui/core"
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from "./constants"

// Cache for loaded catalogs to avoid re-importing
const catalogCache: Record<string, Messages> = {}

export async function loadCatalog(locale: string): Promise<void> {
  // Validate locale
  if (!SUPPORTED_LANGUAGES.includes(locale)) {
    console.warn(`Unsupported locale: ${locale}, falling back to ${DEFAULT_LANGUAGE}`)
    locale = DEFAULT_LANGUAGE
  }

  // Check cache first
  if (locale in catalogCache) {
    i18n.load(locale, catalogCache[locale])
    i18n.activate(locale)
    return
  }

  try {
    // Dynamically import the compiled catalog
    const { messages } = (await import(`./${locale}/messages.ts`)) as {
      messages: Messages
    }

    // Cache the messages
    catalogCache[locale] = messages

    // Load and activate the locale
    i18n.load(locale, messages)
    i18n.activate(locale)
  } catch (error) {
    console.error(`Failed to load catalog for locale: ${locale}`, error)

    // Fallback to default language if not already trying default
    if (locale !== DEFAULT_LANGUAGE) {
      await loadCatalog(DEFAULT_LANGUAGE)
    }
  }
}

// Preload a catalog without activating it
export async function preloadCatalog(locale: string): Promise<void> {
  if (locale in catalogCache || !SUPPORTED_LANGUAGES.includes(locale)) {
    return
  }

  try {
    const module = (await import(`./${locale}/messages.ts`)) as {
      messages: Messages
    }
    const { messages } = module
    catalogCache[locale] = messages
  } catch (error) {
    console.error(`Failed to preload catalog for locale: ${locale}`, error)
  }
}

// Get all loaded locales
export function getLoadedLocales(): string[] {
  return Object.keys(catalogCache)
}

// Clear the catalog cache (useful for testing)
export function clearCatalogCache(): void {
  Object.keys(catalogCache).forEach((key) => delete catalogCache[key])
}
